import { useEffect, useState } from "react";
import { useAccount } from "../../controllers/useAccount";
import { useWorkspaces } from "../../controllers/useWorkspaces";
import { useWorkspaceAi } from "../../controllers/useWorkspaceAi";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { UserCell, UserDirError } from "../components/UserCell";
import type { Project, WorkspaceRole } from "../../models/account";
import { formatCostMicros, formatTokens } from "../../models/ai";

const ROLE_LABEL: Record<WorkspaceRole, string> = { ADMIN: "관리자", MEMBER: "멤버" };
const INV_STATUS_LABEL: Record<string, string> = {
  PENDING: "대기 중", ACCEPTED: "수락됨", DECLINED: "거절됨", REVOKED: "철회됨", EXPIRED: "만료됨",
};

// One workspace: members (list/change-role/remove), invitations (list/invite/revoke), and its
// projects (list/create/open).
//
// The live host is the project shell: 워크스페이스 관리 in the rail mounts this directly against the
// nav's own workspace controller. `WorkspacesView` (its original host, which nests it in a
// list⇄detail state machine) is currently not routed anywhere. Hence the injectable navigation —
// with the shell's defaults the header's back button and the projects tab would push the controller
// into nested screens the shell never renders.
export function WorkspaceDetailView({ ws, onBack, onOpenProject }: {
  ws: ReturnType<typeof useWorkspaces>;
  onBack?: () => void;
  onOpenProject?: (project: Project) => void;
}) {
  const workspace = ws.workspace!;
  const { account } = useAccount();
  const [tab, setTab] = useState<"members" | "invitations" | "projects" | "ai">("members");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The server refuses anyone but the owner, so an admin would only ever meet a 403 here. Showing
  // the button to them anyway would advertise an action they cannot take on someone else's records.
  const isOwner = !!account && account.id === workspace.ownerId;

  return (
    <div className="detail">
      <div className="detail-name">
        <h1>{workspace.name}</h1>
        <span className="mono">{workspace.slug}</span>
        <span className="badge ok">{workspace.status === "ACTIVE" ? "활성" : "보관됨"}</span>
      </div>
      <p className="meta">{workspace.description || "설명 없음"}</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={onBack ?? ws.backToList}>
          {onBack ? "← 홈" : "← 워크스페이스 목록"}
        </button>
        {workspace.status === "ACTIVE" && (
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => {
            if (window.confirm("이 워크스페이스를 보관하시겠습니까?")) void ws.archiveCurrentWorkspace();
          }}>보관</button>
        )}
      </div>

      {ws.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {ws.error}</div>}

      <div className="tabs">
        <button className={`tab ${tab === "members" ? "active" : ""}`} onClick={() => setTab("members")}>멤버 · {ws.members.length}</button>
        <button className={`tab ${tab === "invitations" ? "active" : ""}`} onClick={() => setTab("invitations")}>초대 · {ws.invitations.length}</button>
        <button className={`tab ${tab === "projects" ? "active" : ""}`} onClick={() => setTab("projects")}>프로젝트 · {ws.projects.length}</button>
        <button className={`tab ${tab === "ai" ? "active" : ""}`} onClick={() => setTab("ai")}>AI</button>
      </div>

      {ws.loadingWorkspace ? (
        <div className="center">불러오는 중…</div>
      ) : tab === "members" ? (
        <MembersTab ws={ws} />
      ) : tab === "invitations" ? (
        <InvitationsTab ws={ws} />
      ) : tab === "ai" ? (
        <AiTab getToken={ws.getToken} workspaceId={workspace.id} />
      ) : (
        <ProjectsTab ws={ws} onOpenProject={onOpenProject} />
      )}

      {isOwner && (
        <div className="danger-zone">
          <div>
            <h4>워크스페이스 삭제</h4>
            <p className="meta">
              프로젝트 {ws.projects.length}개와 그 안의 문서·이슈·채팅·첨부파일이 모두 영구 삭제됩니다.
              되돌릴 수 없습니다. 잠시 사용을 멈추려면 대신 <b>보관</b>을 쓰세요.
            </p>
          </div>
          <button className="btn danger" style={{ width: "auto" }} onClick={() => setConfirmingDelete(true)}>
            삭제
          </button>
        </div>
      )}

      {confirmingDelete && (
        <DeleteWorkspaceDialog
          name={workspace.name}
          projectCount={ws.projects.length}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            const deleted = await ws.deleteCurrentWorkspace();
            // On failure the dialog stays open with the error visible above it; closing it would
            // hide the reason the workspace is still there.
            if (deleted) setConfirmingDelete(false);
          }}
          error={ws.error}
        />
      )}
    </div>
  );
}

/**
 * Type-the-name confirmation. A workspace holds a company's entire quality record, so an accidental
 * double-click on a plain confirm() would be unrecoverable — the name has to be typed out.
 */
function DeleteWorkspaceDialog({ name, projectCount, onCancel, onConfirm, error }: {
  name: string;
  projectCount: number;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  error: string | null;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === name.trim();

  return (
    <div className="wz-overlay" role="dialog" aria-modal="true" aria-label="워크스페이스 삭제 확인"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="wz-card" style={{ width: 480 }}>
        <div className="wz-head">
          <h2>워크스페이스 삭제</h2>
          <button className="wz-x" aria-label="닫기" disabled={busy} onClick={onCancel}>✕</button>
        </div>
        <div className="wz-body">
          {error && <div className="wz-error">{error}</div>}
          <p style={{ margin: 0 }}>
            <b>{name}</b> 워크스페이스와 프로젝트 {projectCount}개, 그리고 그 안의 모든 문서·리비전·이슈·채팅·
            첨부파일이 영구 삭제됩니다. <b>복구할 수 없습니다.</b>
          </p>
          <label className="wz-field">
            <span className="wz-label">확인을 위해 워크스페이스 이름을 입력하세요</span>
            <input
              className="wz-input"
              value={typed}
              placeholder={name}
              autoFocus
              disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        </div>
        <div className="wz-foot">
          <button className="btn ghost" style={{ width: "auto" }} disabled={busy} onClick={onCancel}>취소</button>
          <button
            className="btn danger"
            style={{ width: "auto" }}
            disabled={!matches || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
          >
            {busy ? "삭제 중…" : "영구 삭제"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Workspace AI settings (opt-in on/off, per-feature) + monthly usage (tokens gauge + real cost).
// Settings changes are workspace-admin only (server-enforced; a non-admin's PATCH 403s and is shown).
function AiTab({ getToken, workspaceId }: { getToken: () => string | null; workspaceId: string }) {
  const ai = useWorkspaceAi(getToken, workspaceId);
  const [model, setModel] = useState<string>("");
  const [cap, setCap] = useState<string>("");
  const s = ai.settings;

  // seed the model/cap inputs once settings load
  useEffect(() => {
    if (!s) return;
    setModel(s.model ?? "");
    setCap(s.monthlyTokenCap != null ? String(s.monthlyTokenCap) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.workspaceId]);

  const used = ai.usage ? ai.usage.totalPromptTokens + ai.usage.totalCompletionTokens : 0;
  const capNum = s?.monthlyTokenCap ?? null;
  const pct = capNum ? Math.min(100, Math.round((used / capNum) * 100)) : null;
  const gaugeCls = pct == null ? "" : pct >= 100 ? "over" : pct >= 80 ? "warn" : "";

  if (ai.loading && !s) return <div className="center">불러오는 중…</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      {ai.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {ai.error}</div>}

      {/* Regulatory / privacy notice — document content is sent to OpenAI */}
      <div className="note" style={{ background: "var(--amber-tint)", marginBottom: 16 }}>
        ⚠️ AI 기능을 켜면 <b>문서 내용(제목·요약·메모 등)이 OpenAI로 전송</b>되어 처리됩니다. AI 생성물은
        <b> 초안</b>이며 규제 기록으로 확정하기 전 사람이 반드시 검토·승인해야 합니다. 관리자만 설정을 변경할 수 있습니다.
      </div>

      {s && (
        <div className="info-card" style={{ marginBottom: 16 }}>
          <h4>설정</h4>
          <ToggleRow label="AI 기능 사용" desc="이 워크스페이스에서 AI 보조 기능 활성화 (기본: 꺼짐)"
            checked={s.enabled} disabled={ai.saving} onChange={(v) => void ai.save({ enabled: v })} />
          <ToggleRow label="문서 AI 요약" desc="문서 상세에서 AI 초안 요약/키포인트 생성 허용"
            checked={s.docSummaryEnabled} disabled={ai.saving || !s.enabled}
            onChange={(v) => void ai.save({ docSummaryEnabled: v })} />

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 14 }}>
            <label className="sdui-field" style={{ minWidth: 180 }}>
              <span className="sdui-label">모델 (선택 · 비우면 기본)</span>
              <input className="sdui-input" placeholder="gpt-4o-mini" value={model} onChange={(e) => setModel(e.target.value)} />
            </label>
            <label className="sdui-field" style={{ minWidth: 160 }}>
              <span className="sdui-label">월 토큰 상한 (선택 · 비우면 무제한)</span>
              <input className="sdui-input" type="number" placeholder="무제한" value={cap} onChange={(e) => setCap(e.target.value)} />
            </label>
            <button className="btn" style={{ width: "auto" }} disabled={ai.saving}
              onClick={() => void ai.save({ model: model.trim() || null, monthlyTokenCap: cap.trim() ? Number(cap) : null })}>
              {ai.saving ? "저장 중…" : "저장"}
            </button>
          </div>
        </div>
      )}

      {/* Monthly usage */}
      <div className="info-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h4 style={{ margin: 0 }}>사용량</h4>
          <input className="sdui-input" type="month" style={{ width: "auto" }} value={ai.period}
            onChange={(e) => ai.setPeriod(e.target.value)} />
        </div>
        {ai.usage && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, margin: "12px 0" }}>
              <div className="stat-tile"><div className="stat-label">총 토큰</div><div className="stat-value">{formatTokens(used)}</div></div>
              <div className="stat-tile"><div className="stat-label">프롬프트 / 완성</div><div className="stat-value" style={{ fontSize: 16 }}>{formatTokens(ai.usage.totalPromptTokens)} / {formatTokens(ai.usage.totalCompletionTokens)}</div></div>
              <div className="stat-tile"><div className="stat-label">실제 비용</div><div className="stat-value">{formatCostMicros(ai.usage.totalCostMicros)}</div></div>
            </div>
            {pct != null && (
              <div className="gauge" style={{ marginBottom: 8 }}>
                <div className={`gauge-track ${gaugeCls}`}><i style={{ width: `${pct}%` }} /></div>
                <div className="gauge-legend">
                  <span>{formatTokens(used)} / {formatTokens(capNum!)} 토큰</span>
                  <span style={{ color: pct >= 100 ? "var(--pri-highest)" : pct >= 80 ? "var(--amber)" : undefined }}>
                    {pct}%{pct >= 100 ? " · 상한 초과" : pct >= 80 ? " · 상한 근접" : ""}
                  </span>
                </div>
              </div>
            )}
            {ai.usage.byModel.length > 0 ? (
              <table className="sdui-table" style={{ marginTop: 8 }}>
                <thead><tr><th>모델</th><th>프롬프트</th><th>완성</th><th>비용</th></tr></thead>
                <tbody>
                  {ai.usage.byModel.map((m) => (
                    <tr key={m.model}>
                      <td className="mono">{m.model}</td>
                      <td>{formatTokens(m.promptTokens)}</td>
                      <td>{formatTokens(m.completionTokens)}</td>
                      <td>{formatCostMicros(m.costMicros)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="sdui-list-empty">이 기간의 사용 내역이 없습니다.</div>}
          </>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, desc, checked, disabled, onChange }: {
  label: string; desc: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="screen-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 0" }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 13, color: "var(--ink3)" }}>{desc}</div>
      </div>
      <label className="switch">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="slider" />
      </label>
    </div>
  );
}

function MembersTab({ ws }: { ws: ReturnType<typeof useWorkspaces> }) {
  const dir = useUserDirectory(ws.getToken);
  useEffect(() => {
    dir.ensure(ws.members.map((m) => m.userId));
  }, [ws.members, dir.ensure]);

  return (
    <>
    <UserDirError dir={dir} />
    <table className="sdui-table">
      <thead><tr><th>멤버</th><th>역할</th><th>가입일</th><th data-align="end">작업</th></tr></thead>
      <tbody>
        {ws.members.map((m) => (
          <tr key={m.id}>
            <td><UserCell id={m.userId} dir={dir} /></td>
            <td>
              <select
                className="sdui-input" style={{ padding: "6px 8px" }}
                value={m.role}
                onChange={(e) => void ws.changeMemberRole(m.userId, e.target.value as WorkspaceRole)}
              >
                {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </td>
            <td>{new Date(m.joinedAt).toLocaleDateString()}</td>
            <td data-align="end">
              <button className="sdui-button sdui-button-secondary" onClick={() => {
                if (window.confirm("이 멤버를 제거하시겠습니까?")) void ws.removeMember(m.userId);
              }}>제거</button>
            </td>
          </tr>
        ))}
      </tbody>
      {ws.members.length === 0 && <tfoot><tr><td colSpan={4} className="sdui-table-empty">멤버가 없습니다.</td></tr></tfoot>}
    </table>
    </>
  );
}

function InvitationsTab({ ws }: { ws: ReturnType<typeof useWorkspaces> }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("MEMBER");
  const [expiresInDays, setExpiresInDays] = useState<number | "">("");
  const [busy, setBusy] = useState(false);

  async function invite() {
    setBusy(true);
    try {
      await ws.inviteMember({ email, role, expiresInDays: expiresInDays === "" ? undefined : expiresInDays });
      setEmail("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="info-card" style={{ marginBottom: 16 }}>
        <h4>새 초대</h4>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <label className="sdui-field" style={{ flex: 2, minWidth: 200 }}>
            <span className="sdui-label">이메일</span>
            <input className="sdui-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="sdui-field" style={{ flex: 1, minWidth: 120 }}>
            <span className="sdui-label">역할</span>
            <select className="sdui-input" value={role} onChange={(e) => setRole(e.target.value as WorkspaceRole)}>
              {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="sdui-field" style={{ flex: 1, minWidth: 120 }}>
            <span className="sdui-label">만료(일, 선택)</span>
            <input className="sdui-input" type="number" min={1} value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value === "" ? "" : Number(e.target.value))} />
          </label>
          <button className="btn primary" style={{ width: "auto" }} disabled={!email || busy} onClick={invite}>
            {busy ? "초대 중…" : "초대"}
          </button>
        </div>
      </div>

      <table className="sdui-table">
        <thead><tr><th>이메일</th><th>역할</th><th>상태</th><th>만료일</th><th data-align="end">작업</th></tr></thead>
        <tbody>
          {ws.invitations.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.email}</td>
              <td>{ROLE_LABEL[inv.role] ?? inv.role}</td>
              <td>{INV_STATUS_LABEL[inv.status] ?? inv.status}</td>
              <td>{new Date(inv.expiresAt).toLocaleDateString()}</td>
              <td data-align="end">
                {inv.status === "PENDING" && (
                  <button className="sdui-button sdui-button-secondary" onClick={() => void ws.revokeInvitation(inv.id)}>철회</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {ws.invitations.length === 0 && <div className="sdui-table-empty">초대 내역이 없습니다.</div>}
    </div>
  );
}

function ProjectsTab({ ws, onOpenProject }: {
  ws: ReturnType<typeof useWorkspaces>;
  onOpenProject?: (project: Project) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const created = await ws.addProject({ name, slug: slug || undefined });
      if (created) { setCreating(false); setName(""); setSlug(""); }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        {!creating ? (
          <button className="btn primary" style={{ width: "auto" }} onClick={() => setCreating(true)}>＋ 새 프로젝트</button>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <label className="sdui-field">
              <span className="sdui-label">이름</span>
              <input className="sdui-input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="sdui-field">
              <span className="sdui-label">슬러그(선택)</span>
              <input className="sdui-input" value={slug} onChange={(e) => setSlug(e.target.value)} />
            </label>
            <button className="btn ghost" style={{ width: "auto" }} onClick={() => setCreating(false)}>취소</button>
            <button className="btn primary" style={{ width: "auto" }} disabled={!name || busy} onClick={submit}>
              {busy ? "생성 중…" : "생성"}
            </button>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {ws.projects.map((p) => (
          <button
            key={p.id} className="screen-row"
            style={{ width: "100%", border: "none", cursor: "pointer", justifyContent: "space-between" }}
            onClick={() => (onOpenProject ? onOpenProject(p) : ws.openProject(p.id))}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="screen-id">{p.slug}</span>
              <span style={{ fontWeight: 500 }}>{p.name}</span>
            </span>
            <span className="badge ok">{p.status === "ACTIVE" ? "활성" : "보관됨"}</span>
          </button>
        ))}
        {ws.projects.length === 0 && <div className="sdui-list-empty">프로젝트가 없습니다.</div>}
      </div>
    </div>
  );
}
